import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { Category } from "../../../../domain/model/Category";
import { GetCategoryUseCase } from "../../../../domain/usecase/category/get/GetUseCase";
import { State } from "../../../../domain/model/ResponseState";
import { CreateCategoryUseCase } from "../../../../domain/usecase/category/create/CreateUseCase";
import { DeleteCategoryUseCase } from "../../../../domain/usecase/category/delete/DeleteUseCase";
import { UpdateCategoryUseCase } from "../../../../domain/usecase/category/update/UpdateUseCase";
import { useNavigate } from "react-router-dom";
import { ROUTE_ENDPOINTS } from "../../../../data/constant/RouteConstants";
import { UploadFileUseCase } from "../../../../domain/usecase/uploadFile/UploadFileUseCase";
import { Attachment } from "../../../../domain/model/Attachment";

import Compressor from "compressorjs";
import { showGlobalLoadingView } from "../../../../core/utils/Utils";
import { Product } from "../../../../domain/model/Product";
import { GetAllCategoriesUseCase } from "../../../../domain/usecase/category/getAll/GetAllUseCase";
import { UploadMultipleFilesUseCase } from "../../../../domain/usecase/uploadFile/UploadMultipleFilesUseCase";
import { CreateProductUseCase } from "../../../../domain/usecase/product/create/CreateUseCase";
import { DeleteProductUseCase } from "../../../../domain/usecase/product/delete/DeleteUseCase";
import { UpdateProductUseCase } from "../../../../domain/usecase/product/update/UpdateUseCase";
import { GetProductUseCase } from "../../../../domain/usecase/product/get/GetUseCase";
import { GetAllStoresUseCase } from "../../../../domain/usecase/store/getAll/GetAllUseCase";
import { Store } from "../../../../domain/model/Store";

export function CategoryDetailsViewModel(
  getProductUseCase: GetProductUseCase,
  createProductUseCase: CreateProductUseCase,
  deleteProductUseCase: DeleteProductUseCase,
  updateProductUseCase: UpdateProductUseCase,
  uploadMutipleFilesUseCase: UploadMultipleFilesUseCase,
  getAllCategoriesUseCase: GetAllCategoriesUseCase,
  getAllStoresUseCase: GetAllStoresUseCase
) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(true);

  const [categories, setCategories] = useState<Array<Category>>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >();

  const [stores, setStores] = useState<Array<Store>>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string | undefined>();

  const [openDialog, setOpenDialog] = useState(false);
  const [product, setProduct] = useState<Product>({ imagesList: [] });

  useEffect(() => {
    fetchStores();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!selectedStoreId) return;
    setProduct((prev) => ({
      ...prev,
      store: selectedStoreId,
    }));
  }, [selectedStoreId]);

  useEffect(() => {
    if (!selectedCategoryId) return;
    setProduct((prev) => ({
      ...prev,
      category: selectedCategoryId,
    }));
  }, [selectedCategoryId]);

  useEffect(() => {
    if (
      !product?.imagesList ||
      (product?.thumbnailIndex != undefined &&
        product.thumbnailIndex < product.imagesList.length)
    )
      return;
    setProduct((prev) => ({
      ...prev,
      thumbnailIndex: 0,
    }));
  }, [product?.imagesList]);

  async function fetchStores() {
    const response = await getAllStoresUseCase.invoke();
    switch (response.responseState) {
      case State.Success:
        if (!response.data || response.data.length == 0) {
          showSnackbar(
            "You need at least one store to create before creating a product",
            "info"
          );
          navigate(ROUTE_ENDPOINTS.HOME_STORES);
          return;
        }
        const storesData = response.data;
        setStores(storesData);
        if (!storesData[0]._id) return;
        setSelectedStoreId(storesData[0]._id);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function fetchCategories() {
    const response = await getAllCategoriesUseCase.invoke();
    switch (response.responseState) {
      case State.Success:
        if (!response.data || response.data.length == 0) {
          showSnackbar(
            "You need at least one category to create before creating a product",
            "info"
          );
          navigate(ROUTE_ENDPOINTS.HOME_CATEGORIES);
          return;
        }
        const categoriesData = response.data;
        setCategories(categoriesData);
        if (!categoriesData[0]._id) return;
        setSelectedCategoryId(categoriesData[0]._id);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function fetchProduct(id: string) {
    const response = await getProductUseCase.invoke(id);
    switch (response.responseState) {
      case State.Success:
        if (!response.data) return;
        setProduct(response.data);
        setSelectedCategoryId((response.data?.category as any)?._id);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function deleteProduct() {
    if (!product?._id) return;

    showGlobalLoadingView(true);
    const response = await deleteProductUseCase.invoke(product?._id);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        navigate(ROUTE_ENDPOINTS.HOME_PRODUCTS);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function saveOrCreateProduct(isNew: boolean) {
    if (!product) return;

    if (!product.name) {
      showSnackbar("You should enter a name");
      return;
    }
    if (!product.goingLiveAt) {
      showSnackbar(
        "You should enter the date that the product will go live at"
      );
      return;
    }
    if (product.totalQty == undefined) {
      showSnackbar("You should enter the quantity");
      return;
    }
    if (product.totalQty < (product.soldQty ?? 0)) {
      showSnackbar("Quantity cannot be less than the sold quantity");
      return;
    }
    if (!product.actualPrice) {
      showSnackbar("You should enter the price");
      return;
    }
    if (!product.minimumPrice) {
      showSnackbar("You should enter the minimum price");
      return;
    }
    if (!product.priceDecreaseRate) {
      showSnackbar("You should enter the price decrease rate");
      return;
    }
    if (!product.imagesList || product.imagesList?.length == 0) {
      showSnackbar("You should upload at least one image");
      return;
    }

    showGlobalLoadingView(true);

    const files = product.imagesList.filter((file) => file instanceof File);
    if (files.length > 0) {
      const alreadyUploadedFiles = product.imagesList
        .filter((file) => !(file instanceof File))
        .flatMap((file: any) => file._id);

      const uploadedFilesids = await uploadMutipleFiles(files);

      if (uploadedFilesids) {
        alreadyUploadedFiles.push(...uploadedFilesids);
        product.imagesList = alreadyUploadedFiles;
      }
    }

    const response = isNew
      ? await createProductUseCase.invoke(product)
      : await updateProductUseCase.invoke(product);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        navigate(ROUTE_ENDPOINTS.HOME_PRODUCTS);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function uploadMutipleFiles(
    files: Array<File>
  ): Promise<Array<string> | undefined> {
    const formData = new FormData();

    await Promise.all(
      files.map(async (file) => {
        const compressedFile: File | Blob = await new Promise(
          (resolve, reject) => {
            new Compressor(file, {
              quality: 0.8,
              maxHeight: 1800,
              maxWidth: 1800,
              success(result) {
                resolve(result);
              },
              error(error) {
                reject(error);
              },
            });
          }
        );
        formData.append("files", compressedFile);
      })
    );

    showGlobalLoadingView(true);
    const response = await uploadMutipleFilesUseCase.invoke(formData);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        return response.data;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function addImages(file: File) {
    if (!file || !product?.imagesList) return;
    if (product.imagesList.length == 6) {
      showSnackbar("You can only choose maximum 6 images");
      return;
    }
    setProduct((prev) => ({
      ...prev,
      imagesList: [...(prev.imagesList ?? []), file],
    }));
  }

  function removeImage(index: number) {
    if (!product?.imagesList) return;
    setProduct((prev) => ({
      ...prev,
      imagesList: (prev.imagesList ?? []).filter((_, idx) => idx != index),
    }));
  }

  function getCategoryById(id: string): Category | undefined {
    if (!categories) return;
    return categories.filter((category) => category._id === id)[0];
  }

  function getCategoryByTitle(title: string): Category | undefined {
    if (!categories) return;
    return categories.filter((category) => category.title === title)[0];
  }

  function getStoreById(id: string): Store | undefined {
    if (!stores) return;
    return stores.filter((store) => store._id === id)[0];
  }

  function getStoreByName(name: string): Store | undefined {
    if (!stores) return;
    return stores.filter((store) => store.name === name)[0];
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" | "info" = "error"
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }

  return {
    isLoading,
    product,
    fetchProduct,
    addImages,
    removeImage,
    setProduct,
    deleteProduct,
    saveOrCreateProduct,
    categories,
    selectedCategoryId,
    setSelectedCategoryId,
    getCategoryById,
    getCategoryByTitle,
    openDialog,
    setOpenDialog,
    stores,
    setSelectedStoreId,
    selectedStoreId,
    getStoreById,
    getStoreByName,
    setLoading
  };
}
